import { levenshteinDistance } from "src/utils/levenshteinDistance";
import { DatafordelerGQL } from "./DatafordelerGQLCollector";
import { WFSUtilsCollector } from "./WFSUtilsCollector";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";

/**
 * @link https://confluence.kds.dk/pages/viewpage.action?pageId=234782998
 * @description Data collector for ADR (Address) services.
 */
export class adrCollector {

    private static credentials = { // Shared token
        token: "adressevaelger123"
    };

    static async geocodeWithQuality(address: string) {
        let bestMatch = (await adrCollector.soegAdresse(address)).fund.find((a) => a.type === "husnummer");
        if (!bestMatch) {
            throw new Error("No matching husnummer found");
        }
        let husnummerData = await adrCollector.lookupHusnumreById(bestMatch?.id ?? "");
        let utm32N = husnummerData.husnummer.adgangspunkt.koordinater;
        let latLng = GenerateGeomUtils.convertFromUTMtoLatLng2([utm32N.x, utm32N.y]);
        let distance = levenshteinDistance(address, bestMatch?.titel ?? "");
        let quality = distanceToQuality(distance);

        return {
            quality: quality,
            distance: distance,
            latlng: latLng ? latLng : undefined,
            washed: bestMatch?.titel
        }
    }

    static async geocode(address: string): Promise<[number, number]> {
        let bestMatch = (await adrCollector.soegAdresse(address)).fund.find((a) => a.type === "husnummer");
        if (!bestMatch) {
            throw new Error("No matching husnummer found");
        }
        let husnummerId = bestMatch.id;
        let husnummerData = await adrCollector.lookupHusnumreById(husnummerId);
        let koordinater = husnummerData.husnummer.adgangspunkt.koordinater;
        return [koordinater.x, koordinater.y];
    }

    static async reverseGeocodeFull(lat: number, lng: number) {
        let jord = await WFSUtilsCollector.bfeUnderPoint([lng, lat]);
        let adresse = (await DatafordelerGQL.EBR.EBRAdresseOgPunkt(jord.sfeNr)).at(0);
        return adresse;
    }

    static async reverseGeocode(lat: number, lng: number): Promise<string | undefined> {
        let adresse = await adrCollector.reverseGeocodeFull(lat, lng);
        return adresse?.betegnelse;
    }

    static async washAddress(address: string): Promise<VaskResponse> {
        let params = {
            address: address,
            token: this.credentials.token
        }
        return (await fetch("https://adressevaelger.dk/vask?" + new URLSearchParams(params))).json();
    }

    static async soegAdresse(query: string): Promise<SoegResponse> {
        let params = {
            tekst: query,
            token: this.credentials.token
        }
        return (await fetch("https://adressevaelger.dk/soeg?" + new URLSearchParams(params))).json();
    }

    static async lookupAdresseById(id: string): Promise<AdresseOpslag> {
        let params = {
            token: this.credentials.token
        }
        return (await fetch(`https://adressevaelger.dk/adresse/${id}?` + new URLSearchParams(params))).json();
    }

    static async lookupHusnumreById(id: string): Promise<HusnumreOpslag> {
        let params = {
            token: this.credentials.token
        }
        return (await fetch(`https://adressevaelger.dk/husnumre/${id}?` + new URLSearchParams(params))).json();
    }

    static async addresserPåJordstykke(matnr: string, ejerlavskode: string | number) {
        let GQL_resp = await DatafordelerGQL.Mat2.AdresserPåJordstykke(matnr, ejerlavskode);
        return GQL_resp.MAT_Jordstykke.nodes.map((node) => {
            return {
                matnr: node.matrikelnummer,
                ejerlavskode: node.jordstykkeLiggerIEjerlav.ejerlavskode,
                ejerlavsnavn: node.jordstykkeLiggerIEjerlav.ejerlavsnavn,
                husnumre: node.id_lokalId_22_Husnummer_jordstykke_ref?.nodes.map((hus) => {
                    return {
                        id: hus.id_lokalId,
                        adgangsadressebetegnelse: hus.adgangsadressebetegnelse,
                        postnummer: hus.husnummerHoererTilIPostnummer?.postnr,
                        postdistrikt: hus.husnummerHoererTilIPostnummer?.navn,
                        kommunekode: hus.husnummerLiggerIKommuneinddeling?.kommunekode,
                        kommune: hus.husnummerLiggerIKommuneinddeling?.navn,
                        adresser: hus.husnummerHarAdresser?.nodes.map((addr) => {
                            return {
                                id: addr.id_lokalId,
                                adressebetegnelse: addr.adressebetegnelse
                            }
                        }) ?? []
                    }
                }) ?? []
            }
        }).at(0)
    }

    /**
     * Tests if string is likely an address.
     * Looks for words like 'gade', 'vej', 'alle', 'vang'
     * @moved From AddressInterface
     * @param text A string
     * @returns True if the string is likely an adress
     */
    static addressTest(text: string): boolean {
        const addressElements = ['vej', 'gade', 'alle', 'allé', 'vang', 'brygge', 'vænge'];
        let textLower = text.toLowerCase();
        let found = addressElements.find((val) => { return textLower.includes(val); });
        return (found ? true : false);
    }

}

type HusnumreOpslag = {
    status: "ok",
    husnummer: AdresseOpslag["adresse"]["husnummer"]
}
type AdresseOpslag = {
    status: "ok",
    adresse: {
        husnummer: {
            adgangspunkt: {
                koordinater: { x: number, y: number } // UTM32N
            }
            postnummer: {

            }
            navngivenvej: {

            }
            navngivenvejkommunedel: {

            }
            navngivenvejpostnummer: {

            }
            supplerendebynavn: {

            }
        }

    }
}

type navngivenvejpostnummer = {
    "id": string,
    "type": "navngivenvejpostnummer",
    "titel": string, //concat
    "vejnavn": string,
    "postnr": string,
    "postdistrikt": string,
    "antal_husnumre": number
}

type husnummer = {
    "id": string,
    "type": "husnummer",
    "titel": string, //vejnavn + hustnummer, postnummer + postdistrikt
    "vejnavn": string,
    "husnummer": string
}

type SoegResponse = {
    status: "ok",
    beskrivelse: "",
    fund: (husnummer | navngivenvejpostnummer)[]
}

type VaskResponse = {
    vaskestatus: {
        kode: number,
        tekst: string
    },
    vaskeresultat: {
        adresse_id_lokalid: string | null,
        adressebetegnelse: string | null,
        virkningfra: string | null,
        virkningtil: string | null,
        status: number | null
    },
    vaskeresultat_historisk: {
        adressebetegnelse: null,
        virkningfra: string | null,
        virkningtil: string | null
    }
}

function distanceToQuality(distance: number): DataQuality {
    switch (true) {
        case distance <= 1:
            return DataQuality.good;
        case distance <= 3:
            return DataQuality.fuzzy;
        case distance <= 6:
            return DataQuality.bad;
        default:
            return DataQuality.unwashed;
    }
}

export enum DataQuality {
    good = "good",
    fuzzy = "fuzzy",
    bad = "bad",
    unwashed = "unwashed",
    error = "error"
}

/*
 "useDawaAddressWash": true,
 "addressServiceURLDawaWash": "https://dawa.aws.dk/datavask/adgangsadresser",
 "addressServiceURLDawaById": "https://dawa.aws.dk/adgangsadresser",

 
 export enum DawaQuality {
     unwashable = "unwashable",
     dawa_matched = "dawa_matched",
     fuzzy_washed = "fuzzy_washed",
     dawa_error = "dawa_error",
     dawa_washed = "dawa_washed"
 }
 
 export type DawaAddressResponse = {
     query: string,
     result: DawaAddressResult
 };
 
 export type DawaAddressResult = {
     quality: DawaQuality,
     distance: number, // Levenshtein distance for result 
     latlng?: number[],
     washed?: string,
     errormessage?: string
 };
 
 export type DawaAutoCompleteResult = {
     tekst: string, // the address
     data: {
         href: string, // HREF to lookup adress information
         id: string, // The id
         vejnavn: string,
         husnr: string,
         postnr: string,
         postnrnavn: string
     }
 };
 
 export type DawaAddressLookupResult = {
     adgangspunkt: {
         koordinater: number[],
     },
     ejerlav: {
         kode: number,
         navn: string
     },
     kommune: {
         kode: string,
         navn: string
     },
     region: {
         kode: string,
         navn: string
     },
     matrikelnr: string
 };
 
 
 */